import { CloudFilesUploadProps } from './CloudFilesUpload.interface';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { ActionBottomSheet, Button } from '$uikit';
import React, { useEffect, useRef, useState } from 'react';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import { useTranslator } from '$hooks';
import './CloudFilesUpload.sass';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { attachActions, attachItemsSelector } from '$store/attach';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { IcGallery28, IcVideo28 } from '$assets';
import { cloudActions, cloudSelector } from '$store/cloud';
import { CloudDefaultAlbums } from '$store/models';

enum CloudActionsGroups {
  UPLOAD = 'upload',
}

enum AttachActions {
  UPLOAD_PHOTO = 'uploadPhoto',
  UPLOAD_VIDEO = 'uploadVideo',
}

export const CloudFilesUpload = ({
  isOpen,
  onActionChange,
  onClose,
}: CloudFilesUploadProps) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(1);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const pickerId = 'cloud';
  const attachments = useSelector((state: RootState) =>
    attachItemsSelector(state, pickerId),
  );
  const isUploading = attachments === 'photo_uploading';
  const { selectedAlbumId } = useSelector(cloudSelector);

  useEffect(() => {
    setIsBottomSheetOpen(!!attachments.length);
  }, [attachments]);

  const actions = [
    {
      label: t('upload_photo'),
      value: AttachActions.UPLOAD_PHOTO,
      groupId: CloudActionsGroups.UPLOAD,
      icon: <IcGallery28 />,
    },
    {
      label: t('upload_video'),
      value: AttachActions.UPLOAD_VIDEO,
      groupId: CloudActionsGroups.UPLOAD,
      icon: <IcVideo28 />,
    },
  ];

  const handlePhotoChange = ({
    target: { files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!files) return;
    attachManager.current?.uploadImages(files);
    setPhotoInputKey(Date.now());
  };

  const handleVideoChange = ({
    target: { files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!files) {
      return;
    }

    attachManager.current?.uploadVideo(files[0]);
    setVideoInputKey(Date.now());
  };

  const handleAction = (value: AttachActions) => {
    onActionChange();

    switch (value) {
      case AttachActions.UPLOAD_PHOTO:
        photoInputRef.current?.click();
        break;

      case AttachActions.UPLOAD_VIDEO:
        videoInputRef.current?.click();
        break;
    }
  };

  const handleCloseBottomSheet = () => {
    dispatch(attachActions.clear(pickerId));
    setIsBottomSheetOpen(false);
  };

  const handleUploadFiles = () => {
    if (!attachments.length || attachments === 'photo_uploading') {
      return;
    }

    const items = attachments.map((attachItem) => ({
      elementId:
        attachItem.type === 'photo'
          ? attachItem.photo!.photoId
          : attachItem.video!.videoId,
      type: attachItem.type,
    }));

    dispatch(
      cloudActions.addFilesToAlbum({
        albumId: +selectedAlbumId > 0 ? selectedAlbumId : CloudDefaultAlbums.UPLOADED,
        items,
      }),
    );
    handleCloseBottomSheet();
  };

  return (
    <div className="CloudFilesUpload">
      <ActionBottomSheet
        title={t('cloud_actions_title')}
        isOpened={isOpen}
        options={actions}
        onChange={(action) => handleAction(action as AttachActions)}
        onClose={onClose}
      />
      <input
        className="Cloud__fileInput"
        ref={photoInputRef}
        key={photoInputKey}
        type="file"
        accept="image/*"
        onChange={handlePhotoChange}
        multiple
      />
      <input
        className="Cloud__fileInput"
        ref={videoInputRef}
        key={videoInputKey}
        type="file"
        accept="video/mp4,video/quicktime"
        onChange={handleVideoChange}
      />
      <div
        className={`CloudFilesUpload_BottomSheet ${isBottomSheetOpen ? '_active' : ''}`}
      >
        <BottomSheet title={t('cloud_upload')} onClose={handleCloseBottomSheet}>
          <AttachPickerManager pickerId={pickerId} ref={attachManager} mini />
          <div className="CloudFilesUpload_BottomSheet_btns">
            <Button disabled={isUploading} onClick={handleUploadFiles}>
              {t('cloud_save')}
            </Button>
          </div>
        </BottomSheet>
      </div>
    </div>
  );
};
